(() => {
  const MOBILE_QUERY = window.matchMedia("(max-width: 989px)");
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class EloAnnouncement extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(
        this.querySelectorAll("[data-elo-announcement-slide]"),
      );
      this.pauseButton = this.querySelector("[data-elo-announcement-pause]");
      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains("is-active")),
      );
      this.paused = false;
      this.timer = 0;
      this.pauseButton?.addEventListener("click", () => this.togglePause());
      document.addEventListener("visibilitychange", () => {
        if (document.hidden) this.stop();
        else this.start();
      });
      this.addEventListener("mouseenter", () => this.stop());
      this.addEventListener("mouseleave", () => this.start());
      this.start();
    }

    start() {
      this.stop();
      if (
        this.paused ||
        this.dataset.autoplay !== "true" ||
        this.slides.length < 2 ||
        document.hidden
      )
        return;
      const duration = Math.max(3000, Number(this.dataset.duration) || 5000);
      this.timer = window.setInterval(
        () => this.show((this.index + 1) % this.slides.length),
        duration,
      );
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = 0;
    }

    show(index) {
      this.index = index;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle("is-active", active);
        slide.setAttribute("aria-hidden", String(!active));
        slide.querySelectorAll("a, button").forEach((item) => {
          item.tabIndex = active ? 0 : -1;
        });
      });
    }

    togglePause() {
      this.paused = !this.paused;
      this.pauseButton?.setAttribute("aria-pressed", String(this.paused));
      this.pauseButton?.setAttribute(
        "aria-label",
        this.paused ? "Продължи съобщенията" : "Пауза на съобщенията",
      );
      this.classList.toggle("is-paused", this.paused);
      if (this.paused) this.stop();
      else this.start();
    }
  }

  if (!customElements.get("elo-announcement"))
    customElements.define("elo-announcement", EloAnnouncement);

  class EloSiteHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === "true") return;
      this.dataset.ready = "true";

      this.section = this.closest(".elo-header-section");
      this.shell = this.querySelector(".elo-header__shell");
      this.headerBar = this.querySelector(".elo-header__bar");
      this.menuButton = this.querySelector("[data-elo-menu-toggle]");
      this.menu = this.querySelector("[data-elo-menu]");
      this.menuFilters = Array.from(
        this.querySelectorAll("[data-elo-menu-filter]"),
      );
      this.megaTrigger = this.querySelector("[data-elo-mega-trigger]");
      this.mega = this.querySelector("[data-elo-mega]");
      this.search = this.querySelector("[data-elo-search]");
      this.searchOpenButton = this.querySelector("[data-elo-search-open]");
      this.searchInput = this.querySelector("[data-elo-search-input]");
      this.searchResults = this.querySelector("[data-elo-search-results]");
      this.searchDefault = this.querySelector("[data-elo-search-default]");
      this.cartButton = this.querySelector("#cart-icon-bubble");
      this.menuOpen = false;
      this.megaOpen = false;
      this.searchOpen = false;
      this.currentScroll = Math.max(0, window.scrollY);
      this.scrollDirection = null;
      this.scrollDistance = 0;
      this.searchTimer = 0;
      this.searchRequest = null;
      this.menuClosing = false;
      this.menuCloseTimer = 0;
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.menuButton?.addEventListener(
        "click",
        () => this.setMenu(!this.menuOpen),
        { signal },
      );
      this.megaTrigger?.addEventListener(
        "click",
        () => this.setMega(!this.megaOpen),
        { signal },
      );
      this.megaTrigger?.addEventListener(
        "mouseenter",
        () => this.setMega(true, false),
        { signal },
      );
      this.shell?.addEventListener(
        "mouseleave",
        () => this.setMega(false, false),
        { signal },
      );
      this.querySelectorAll("[data-elo-mega-link]").forEach((link) =>
        link.addEventListener("click", () => this.setMega(false, false), {
          signal,
        }),
      );
      this.searchOpenButton?.addEventListener(
        "click",
        () => this.setSearch(true),
        { signal },
      );
      this.querySelectorAll("[data-elo-search-close]").forEach((button) =>
        button.addEventListener("click", () => this.setSearch(false), {
          signal,
        }),
      );
      this.querySelectorAll("[data-elo-menu-link]").forEach((link) =>
        link.addEventListener("click", () => this.setMenu(false, false), {
          signal,
        }),
      );
      this.menuFilters.forEach((button) => {
        button.addEventListener(
          "click",
          () => this.activateMenuFilter(button),
          { signal },
        );
        button.addEventListener(
          "keydown",
          (event) => {
            if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key))
              return;
            event.preventDefault();
            const tablist = button.closest('[role="tablist"]');
            const group = Array.from(
              tablist?.querySelectorAll("[data-elo-menu-filter]") || [],
            );
            const index = group.indexOf(button);
            let nextIndex = index;
            if (event.key === "ArrowRight")
              nextIndex = (index + 1) % group.length;
            if (event.key === "ArrowLeft")
              nextIndex = (index - 1 + group.length) % group.length;
            if (event.key === "Home") nextIndex = 0;
            if (event.key === "End") nextIndex = group.length - 1;
            const nextButton = group[nextIndex];
            this.activateMenuFilter(nextButton);
            nextButton?.focus();
          },
          { signal },
        );
      });
      if (this.menuFilters.length)
        this.activateMenuFilter(this.menuFilters[0], false);
      this.addEventListener(
        "click",
        (event) => {
          const cartButton = event.target.closest("#cart-icon-bubble");
          if (cartButton && this.contains(cartButton))
            this.openCart(event, cartButton);
        },
        { signal },
      );
      this.searchInput?.addEventListener("input", () => this.queueSearch(), {
        signal,
      });
      this.addEventListener("keydown", (event) => this.onKeyDown(event), {
        signal,
      });
      document.addEventListener(
        "pointerdown",
        (event) => {
          if (this.megaOpen && !this.contains(event.target))
            this.setMega(false, false);
        },
        { signal },
      );
      window.addEventListener("scroll", () => this.onScroll(), {
        passive: true,
        signal,
      });
      window.addEventListener(
        "resize",
        () => {
          this.updateOverlayGeometry();
          this.positionMenuIndicators();
        },
        { passive: true, signal },
      );
      MOBILE_QUERY.addEventListener("change", () => this.onBreakpointChange(), {
        signal,
      });
      this.updateScrollState();
      window.requestAnimationFrame(() => this.positionMenuIndicators());

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        this.cartObserver = new MutationObserver(() => {
          const active = cartDrawer.classList.contains("active");
          document.body.classList.toggle("elo-cart-open", active);
          this.querySelector("#cart-icon-bubble")?.setAttribute(
            "aria-expanded",
            String(active),
          );
          if (active) {
            this.setMega(false, false);
            this.setMenu(false, false);
            this.setSearch(false, false);
            this.revealHeader();
          }
          this.syncPageLock();
        });
        this.cartObserver.observe(cartDrawer, {
          attributes: true,
          attributeFilter: ["class"],
        });
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.cartObserver?.disconnect();
      window.clearTimeout(this.searchTimer);
      window.clearTimeout(this.menuCloseTimer);
      this.searchRequest?.abort();
    }

    setMenu(open, focus = true) {
      if (!MOBILE_QUERY.matches && open) return;
      if (this.menuOpen === open) return;
      window.clearTimeout(this.menuCloseTimer);

      if (open) {
        this.menuClosing = false;
        this.classList.remove("is-menu-closing");
        this.setMega(false, false);
        this.setSearch(false, false);
        const cartDrawer = document.querySelector("cart-drawer.active");
        cartDrawer?.close?.();
        this.updateOverlayGeometry();

        this.menuOpen = true;
        this.classList.add("is-menu-open");
        document.body.classList.add("elo-menu-open");
        this.menuButton?.setAttribute("aria-expanded", "true");
        this.menuButton?.setAttribute("aria-label", "Затвори менюто");
        this.menu?.setAttribute("aria-hidden", "false");
        this.menu?.removeAttribute("inert");
        this.updateScrollState();
        this.revealHeader();
        this.syncPageLock();
        window.requestAnimationFrame(() => this.positionMenuIndicators());
        return;
      }

      this.menuOpen = false;
      this.menuClosing = true;
      this.classList.add("is-menu-closing");
      this.classList.remove("is-menu-open");
      document.body.classList.remove("elo-menu-open");
      this.menuButton?.setAttribute("aria-expanded", "false");
      this.menuButton?.setAttribute("aria-label", "Отвори менюто");
      this.menu?.setAttribute("aria-hidden", "false");
      this.menu?.setAttribute("inert", "");
      this.updateScrollState();
      this.revealHeader();
      this.syncPageLock();
      this.menuCloseTimer = window.setTimeout(() => {
        this.menuClosing = false;
        this.classList.remove("is-menu-closing");
        this.menu?.setAttribute("aria-hidden", "true");
        this.syncPageLock();
      }, 1000);
      if (focus) this.menuButton?.focus({ preventScroll: true });
    }

    setMega(open, focus = true) {
      if (MOBILE_QUERY.matches && open) return;
      if (this.megaOpen === open) return;
      if (open) {
        this.setMenu(false, false);
        this.setSearch(false, false);
        const cartDrawer = document.querySelector("cart-drawer.active");
        cartDrawer?.close?.();
      }

      this.megaOpen = open;
      this.classList.toggle("is-mega-open", open);
      this.megaTrigger?.setAttribute("aria-expanded", String(open));
      this.mega?.setAttribute("aria-hidden", String(!open));
      if (open) this.mega?.removeAttribute("inert");
      else this.mega?.setAttribute("inert", "");
      this.updateScrollState();
      this.revealHeader();
      if (open)
        window.requestAnimationFrame(() => this.positionMenuIndicators());
      if (!open && focus) this.megaTrigger?.focus({ preventScroll: true });
    }

    setSearch(open, focus = true) {
      if (this.searchOpen === open) return;
      if (open) {
        this.setMega(false, false);
        this.setMenu(false, false);
        window.clearTimeout(this.menuCloseTimer);
        this.menuClosing = false;
        this.classList.remove("is-menu-closing");
        this.menu?.setAttribute("aria-hidden", "true");
        const cartDrawer = document.querySelector("cart-drawer.active");
        cartDrawer?.close?.();
      }

      this.searchOpen = open;
      this.classList.toggle("is-search-open", open);
      document.body.classList.toggle("elo-search-open", open);
      this.searchOpenButton?.setAttribute("aria-expanded", String(open));
      this.search?.setAttribute("aria-hidden", String(!open));
      if (open) {
        this.search?.removeAttribute("inert");
        this.revealHeader();
        window.requestAnimationFrame(() =>
          this.searchInput?.focus({ preventScroll: true }),
        );
      } else {
        this.search?.setAttribute("inert", "");
        if (focus) this.searchOpenButton?.focus({ preventScroll: true });
      }
      this.syncPageLock();
    }

    syncPageLock() {
      const cartOpen = document.querySelector("cart-drawer.active");
      const headerOverlayOpen =
        this.menuOpen || this.menuClosing || this.searchOpen;
      this.section?.classList.toggle("is-overlay-active", headerOverlayOpen);
      document.documentElement.classList.toggle(
        "elo-overlay-open",
        headerOverlayOpen || Boolean(cartOpen),
      );
    }

    updateOverlayGeometry() {
      if (!this.section || !this.headerBar || !MOBILE_QUERY.matches) return;
      const top = Math.max(8, this.section.getBoundingClientRect().top);
      this.style.setProperty("--elo-overlay-top", `${top}px`);
      this.style.setProperty(
        "--elo-overlay-bar-height",
        `${this.headerBar.getBoundingClientRect().height}px`,
      );
    }

    updateScrollState() {
      this.classList.toggle(
        "is-scrolled",
        window.scrollY > 18 || this.menuOpen || this.megaOpen,
      );
    }

    activateMenuFilter(selectedButton, animate = true) {
      const category = selectedButton.dataset.eloMenuFilter || "all";
      this.menuFilters.forEach((button) => {
        const active = button.dataset.eloMenuFilter === category;
        button.setAttribute("aria-selected", String(active));
        button.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll("[data-elo-menu-category]").forEach((product) => {
        const visible =
          category === "all" || product.dataset.eloMenuCategory === category;
        product.classList.toggle("is-category-hidden", !visible);
        product.setAttribute("aria-hidden", String(!visible));
        if (visible) product.removeAttribute("inert");
        else product.setAttribute("inert", "");
      });
      this.classList.toggle("is-filter-animating", animate);
      window.requestAnimationFrame(() => this.positionMenuIndicators());
      if (animate)
        window.setTimeout(
          () => this.classList.remove("is-filter-animating"),
          520,
        );
    }

    positionMenuIndicators() {
      this.querySelectorAll("[data-elo-menu-indicator]").forEach(
        (indicator) => {
          const tablist = indicator.closest('[role="tablist"]');
          const active = tablist?.querySelector(
            '[data-elo-menu-filter][aria-selected="true"]',
          );
          if (!active) return;
          indicator.style.setProperty("--elo-tab-x", `${active.offsetLeft}px`);
          indicator.style.setProperty("--elo-tab-w", `${active.offsetWidth}px`);
        },
      );
    }

    openCart(event, cartButton = this.querySelector("#cart-icon-bubble")) {
      const cartDrawer = document.querySelector("cart-drawer");
      this.setMega(false, false);
      this.setMenu(false, false);
      this.setSearch(false, false);

      event.preventDefault();
      if (!cartDrawer?.open) {
        window.location.assign(window.routes?.cart_url || "/cart");
        return;
      }
      this.revealHeader();
      cartDrawer.open(cartButton);
    }

    onKeyDown(event) {
      if (event.key === "Escape") {
        if (this.searchOpen) {
          event.preventDefault();
          this.setSearch(false);
        } else if (this.menuOpen) {
          event.preventDefault();
          this.setMenu(false);
        } else if (this.megaOpen) {
          event.preventDefault();
          this.setMega(false);
        }
        return;
      }

      const container = this.searchOpen
        ? this.search
        : this.menuOpen
          ? this.menu
          : null;
      if (!container || event.key !== "Tab") return;
      const focusable = Array.from(
        container.querySelectorAll(FOCUSABLE),
      ).filter((node) => node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onScroll() {
      this.updateScrollState();
      if (
        !MOBILE_QUERY.matches ||
        this.dataset.scrollBehavior === "always" ||
        this.menuOpen ||
        this.megaOpen ||
        this.searchOpen ||
        document.querySelector("cart-drawer.active")
      )
        return;
      const next = Math.max(0, window.scrollY);
      const delta = next - this.currentScroll;
      if (next <= 16) {
        this.revealHeader();
        this.scrollDirection = null;
        this.scrollDistance = 0;
        this.currentScroll = next;
        return;
      }
      if (Math.abs(delta) < 2) {
        this.currentScroll = next;
        return;
      }

      const direction = delta > 0 ? "down" : "up";
      if (direction !== this.scrollDirection) {
        this.scrollDirection = direction;
        this.scrollDistance = 0;
      }
      this.scrollDistance += Math.abs(delta);
      if (direction === "down" && next > 120 && this.scrollDistance >= 18) {
        this.section?.classList.add("is-scroll-hidden");
        this.scrollDistance = 0;
      } else if (direction === "up" && this.scrollDistance >= 10) {
        this.revealHeader();
        this.scrollDistance = 0;
      }
      this.currentScroll = next;
    }

    revealHeader() {
      this.section?.classList.remove("is-scroll-hidden");
    }

    onBreakpointChange() {
      this.setMenu(false, false);
      this.setMega(false, false);
      this.revealHeader();
      this.updateOverlayGeometry();
      this.updateScrollState();
      this.currentScroll = Math.max(0, window.scrollY);
      this.scrollDirection = null;
      this.scrollDistance = 0;
    }

    queueSearch() {
      window.clearTimeout(this.searchTimer);
      const query = this.searchInput?.value.trim() || "";
      if (query.length < 2) {
        this.searchRequest?.abort();
        this.searchResults?.replaceChildren();
        if (this.searchDefault) this.searchDefault.hidden = false;
        return;
      }
      this.searchTimer = window.setTimeout(
        () => this.fetchSuggestions(query),
        180,
      );
    }

    async fetchSuggestions(query) {
      this.searchRequest?.abort();
      this.searchRequest = new AbortController();
      try {
        const params = new URLSearchParams({
          q: query,
          "resources[type]": "product,page,article",
          "resources[limit]": "4",
          "resources[options][unavailable_products]": "hide",
        });
        const response = await fetch(
          `${window.routes?.predictive_search_url || "/search/suggest"}.json?${params}`,
          {
            signal: this.searchRequest.signal,
            headers: { Accept: "application/json" },
          },
        );
        if (!response.ok) throw new Error("Search unavailable");
        const data = await response.json();
        const results = data?.resources?.results || {};
        const entries = [
          ...(results.products || []).map((item) => ({
            ...item,
            resultType: "Продукт",
          })),
          ...(results.pages || []).map((item) => ({
            ...item,
            resultType: "Страница",
          })),
          ...(results.articles || []).map((item) => ({
            ...item,
            resultType: "Статия",
          })),
        ];
        this.renderSuggestions(entries.slice(0, 8), query);
      } catch (error) {
        if (error.name !== "AbortError") this.renderSuggestions([], query);
      }
    }

    renderSuggestions(entries, query) {
      if (!this.searchResults) return;
      this.searchResults.replaceChildren();
      if (this.searchDefault) this.searchDefault.hidden = true;

      if (!entries.length) {
        const empty = document.createElement("p");
        empty.className = "elo-search__hint";
        empty.textContent = `Няма намерени продукти за „${query}“.`;
        this.searchResults.append(empty);
        return;
      }

      entries.forEach((entry) => {
        const link = document.createElement("a");
        link.className = "elo-search-result";
        link.href = entry.url;
        if (entry.featured_image?.url) {
          const image = document.createElement("img");
          image.src = entry.featured_image.url;
          image.alt = entry.featured_image.alt || entry.title;
          image.loading = "lazy";
          link.append(image);
        } else {
          const spacer = document.createElement("span");
          spacer.className = "elo-search-result__type";
          spacer.textContent = entry.resultType?.slice(0, 1) || "É";
          link.append(spacer);
        }
        const copy = document.createElement("span");
        const title = document.createElement("strong");
        title.textContent = entry.resultType === "Продукт"
          ? entry.title.replace(/^Donut\s+/i, "")
          : entry.title;
        const price = document.createElement("small");
        price.textContent = entry.price || entry.resultType || "Виж резултата";
        copy.append(title, price);
        const arrow = document.createElement("span");
        arrow.textContent = "→";
        arrow.setAttribute("aria-hidden", "true");
        link.append(copy, arrow);
        this.searchResults.append(link);
      });
    }
  }

  if (!customElements.get("elo-site-header"))
    customElements.define("elo-site-header", EloSiteHeader);

  const formatIndex = (value) => String(value).padStart(2, "0");

  document.querySelectorAll("[data-elo-product-card]").forEach((card) => {
    const selectedImage = card.querySelector(
      ".elo-product-card__image--selected, .elo-collection-card__image--selected",
    );
    const links = card.querySelectorAll("[data-elo-card-link]");
    const price = card.querySelector("[data-elo-card-price]");
    const addPrice = card.querySelector("[data-elo-add-price]");
    const input = card.querySelector("[data-elo-card-variant-input]");
    const variantTitle = card.querySelector("[data-elo-card-variant-title]");

    card.querySelectorAll("[data-elo-card-variant]").forEach((button) => {
      button.addEventListener("click", () => {
        card.querySelectorAll("[data-elo-card-variant]").forEach((item) => {
          const selected = item === button;
          item.classList.toggle("is-selected", selected);
          item.setAttribute("aria-pressed", String(selected));
        });
        if (selectedImage && button.dataset.variantImage) {
          selectedImage.src = button.dataset.variantImage;
          if (button.dataset.variantSrcset)
            selectedImage.srcset = button.dataset.variantSrcset;
          selectedImage.alt = button.dataset.variantAlt || selectedImage.alt;
        }
        links.forEach((link) => {
          link.href = button.dataset.variantUrl;
        });
        if (price) price.textContent = button.dataset.variantPrice;
        if (addPrice) addPrice.textContent = button.dataset.variantPrice;
        if (variantTitle)
          variantTitle.textContent = button.dataset.variantTitle || "";
        if (input) input.value = button.dataset.variantId;
      });
    });
  });

  document.querySelectorAll("[data-elo-product-form]").forEach((form) => {
    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (form.classList.contains("is-loading")) return;
      form.classList.add("is-loading");
      const button = form.querySelector('button[type="submit"]');
      const label = form.querySelector("[data-elo-add-label]");
      const originalLabel = label?.textContent;
      if (label) label.textContent = "ДОБАВЯНЕ…";

      try {
        const variantId = Number(form.querySelector('[name="id"]')?.value);
        const response = await fetch(
          window.routes?.cart_add_url || "/cart/add.js",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }],
              sections: ["cart-drawer", "cart-icon-bubble"],
              sections_url: window.location.pathname,
            }),
          },
        );
        const data = await response.json();
        if (!response.ok)
          throw new Error(data.description || "Неуспешно добавяне");
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer?.renderContents) {
          cartDrawer.renderContents(data);
          cartDrawer.open();
        } else {
          window.location.href = window.routes?.cart_url || "/cart";
        }
        if (label) label.textContent = "ДОБАВЕНО";
      } catch (error) {
        if (label) label.textContent = error.message || "ОПИТАЙ ОТНОВО";
      } finally {
        window.setTimeout(() => {
          form.classList.remove("is-loading");
          if (label) label.textContent = originalLabel;
          button?.removeAttribute("aria-disabled");
        }, 1300);
      }
    });
  });

  document.querySelectorAll("[data-elo-product-rail]").forEach((rail) => {
    const scope = rail.closest(".elo-home__products") || rail.parentElement;
    const wrap = rail.closest("[data-elo-rail-wrap]");
    const cursor = wrap?.querySelector("[data-elo-swipe-cursor]");
    const prev = scope?.querySelector("[data-elo-rail-prev]");
    const next = scope?.querySelector("[data-elo-rail-next]");
    const status = scope?.querySelector("[data-elo-rail-status]");
    const progress = scope?.querySelector("[data-elo-rail-progress]");
    const cards = Array.from(rail.querySelectorAll(".elo-product-card"));
    const getIndex = () => {
      if (!cards.length) return 0;
      const railLeft = rail.getBoundingClientRect().left;
      let closest = 0;
      let distance = Infinity;
      cards.forEach((card, index) => {
        const value = Math.abs(card.getBoundingClientRect().left - railLeft);
        if (value < distance) {
          distance = value;
          closest = index;
        }
      });
      return closest;
    };
    const update = () => {
      const index = getIndex();
      if (status)
        status.textContent = `${formatIndex(index + 1)} / ${formatIndex(cards.length)}`;
      if (progress)
        progress.parentElement?.style.setProperty(
          "--elo-rail-progress",
          `${((index + 1) / Math.max(cards.length, 1)) * 100}%`,
        );
    };
    const move = (direction) => {
      if (!cards.length) return;
      let index = getIndex() + direction;
      if (index >= cards.length) index = 0;
      if (index < 0) index = cards.length - 1;
      const railPadding = Number.parseFloat(
        window.getComputedStyle(rail).paddingInlineStart,
      ) || 0;
      rail.scrollTo({
        left: Math.max(0, cards[index].offsetLeft - railPadding),
        behavior: "smooth",
      });
    };
    prev?.addEventListener("click", () => move(-1));
    next?.addEventListener("click", () => move(1));
    let updateFrame = 0;
    rail.addEventListener("scroll", () => {
      window.cancelAnimationFrame(updateFrame);
      updateFrame = window.requestAnimationFrame(update);
    }, { passive: true });
    if (
      wrap &&
      cursor &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches
    ) {
      wrap.addEventListener("pointerleave", () =>
        cursor.classList.remove("is-visible"),
      );
      wrap.addEventListener("pointermove", (event) => {
        const bounds = wrap.getBoundingClientRect();
        cursor.style.setProperty(
          "--elo-cursor-x",
          `${event.clientX - bounds.left}px`,
        );
        cursor.style.setProperty(
          "--elo-cursor-y",
          `${event.clientY - bounds.top}px`,
        );
        cursor.classList.add("is-visible");
        cursor.classList.toggle(
          "is-muted",
          Boolean(event.target.closest("button, a, input, label")),
        );
      });
    }

    let dragStartX = 0;
    let dragStartScroll = 0;
    let dragging = false;
    let dragMoved = false;
    rail.addEventListener("pointerdown", (event) => {
      if (!window.matchMedia("(pointer: fine)").matches) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (event.target.closest("button, input, label, form")) return;
      dragging = true;
      dragMoved = false;
      dragStartX = event.clientX;
      dragStartScroll = rail.scrollLeft;
      rail.setPointerCapture?.(event.pointerId);
      rail.classList.add("is-dragging");
      if (cursor) cursor.textContent = "drag";
    });
    rail.addEventListener("pointermove", (event) => {
      if (!dragging) return;
      if (Math.abs(event.clientX - dragStartX) > 5) dragMoved = true;
      rail.scrollLeft = dragStartScroll - (event.clientX - dragStartX);
      event.preventDefault();
    });
    const finishDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      rail.releasePointerCapture?.(event.pointerId);
      rail.classList.remove("is-dragging");
      if (cursor) cursor.textContent = "swipe";
      window.setTimeout(() => {
        dragMoved = false;
      });
    };
    rail.addEventListener("pointerup", finishDrag);
    rail.addEventListener("pointercancel", finishDrag);
    rail.addEventListener(
      "click",
      (event) => {
        if (!dragMoved) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true,
    );
    update();
  });

  document.querySelectorAll("[data-elo-community]").forEach((scope) => {
    const rail = scope.querySelector("[data-elo-community-rail]");
    const cards = Array.from(rail?.children || []);
    const prev = scope.querySelector("[data-elo-community-prev]");
    const next = scope.querySelector("[data-elo-community-next]");
    const status = scope.querySelector("[data-elo-community-status]");
    const progress = scope.querySelector("[data-elo-community-progress]");
    if (!rail || !cards.length) return;

    const getIndex = () => {
      const left = rail.getBoundingClientRect().left;
      return cards.reduce(
        (best, card, index) =>
          Math.abs(card.getBoundingClientRect().left - left) <
          Math.abs(cards[best].getBoundingClientRect().left - left)
            ? index
            : best,
        0,
      );
    };
    const update = () => {
      const index = getIndex();
      if (status)
        status.textContent = `${formatIndex(index + 1)} / ${formatIndex(cards.length)}`;
      if (progress)
        progress.style.width = `${((index + 1) / cards.length) * 100}%`;
    };
    const move = (direction) => {
      let index = getIndex() + direction;
      if (index >= cards.length) index = 0;
      if (index < 0) index = cards.length - 1;
      cards[index]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    };
    prev?.addEventListener("click", () => move(-1));
    next?.addEventListener("click", () => move(1));
    rail.addEventListener(
      "scroll",
      () => window.requestAnimationFrame(update),
      { passive: true },
    );
    update();
  });

  document.querySelectorAll("[data-elo-values]").forEach((scope) => {
    const tabs = Array.from(scope.querySelectorAll("[data-elo-value-tab]"));
    const panels = Array.from(scope.querySelectorAll("[data-elo-value-panel]"));
    const images = Array.from(scope.querySelectorAll("[data-elo-value-image]"));
    const activate = (tab, focus = false) => {
      const key = tab.dataset.eloValueTab;
      tabs.forEach((item) => {
        const active = item === tab;
        item.setAttribute("aria-selected", String(active));
        item.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.eloValuePanel !== key;
      });
      images.forEach((image) => {
        image.hidden = image.dataset.eloValueImage !== key;
      });
      scope.dataset.activeValue = key;
      if (focus) tab.focus();
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => activate(tab));
      tab.addEventListener("mouseenter", () => {
        if (window.matchMedia("(hover: hover)").matches) activate(tab);
      });
      tab.addEventListener("keydown", (event) => {
        if (!["ArrowUp", "ArrowDown", "Home", "End"].includes(event.key))
          return;
        event.preventDefault();
        let next = index;
        if (event.key === "ArrowDown") next = (index + 1) % tabs.length;
        if (event.key === "ArrowUp")
          next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === "Home") next = 0;
        if (event.key === "End") next = tabs.length - 1;
        activate(tabs[next], true);
      });
    });
  });

  const revealItems = document.querySelectorAll(
    "[data-elo-reveal], .elo-product-card",
  );
  if (
    "IntersectionObserver" in window &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
    );
    revealItems.forEach((item) => observer.observe(item));
  } else {
    revealItems.forEach((item) => item.classList.add("is-visible"));
  }
})();
